import { DestroyRef, Injectable, signal } from '@angular/core';
import {
  AssignmentSectionQuestionQuery,
  AssignmentSectionQuestionService,
  AssignmentSectionQuestionUIModel,
} from '@examdojo/assignment';
import {
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityQuestionAttemptStatus,
  ChallengeActivityQuestionAttemptStoreModel,
  ChallengeActivityService,
  ChallengeActivityUIModel,
} from '@examdojo/challenge';
import { shareOneReplay } from '@examdojo/core/rxjs';
import {
  calculateQuestionTotalMarks,
  getGradingResultAwardedMarksFromQuestionItems,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityUIModel,
  QuestionAttemptStoreModel,
} from '@examdojo/question';
import {
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  switchMap,
} from 'rxjs';

export interface QuestionsWithAttemptStatuses {
  assignmentSectionQuestion: AssignmentSectionQuestionUIModel;
  attemptStatus: ChallengeActivityQuestionAttemptStatus;
  questionAttemptStatus: QuestionAttemptStoreModel['status'] | null;
  questionGradingResult: {
    awardedMarks: number;
    totalMarks: number;
  } | null;
}

@Injectable()
export class ChallengeActivityQuestionSelectorService {
  constructor(
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly assignmentSectionQuestionService: AssignmentSectionQuestionService,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly isTransitioningBetweenQuestions = signal(false);

  readonly questionsWithAttemptStatuses$: Observable<QuestionsWithAttemptStatuses[]> =
    this.assignmentSectionQuestionQuery.entities$.pipe(
      switchMap((questions) => {
        return this.challengeActivityQuestionAttemptQuery.entities$.pipe(
          switchMap((attempts) => {
            return this.questionAttemptQuery.entities$.pipe(
              map((questionAttemptEntities) => {
                return questionAttemptEntities.filter((entity) =>
                  attempts.some((attempt) => attempt.question_id === entity.attempt.question_id),
                );
              }),
              map((questionAttemptEntities) => {
                return this.mapQuestionAttemptsToQuestionsWithAttemptStatuses(
                  questions,
                  attempts,
                  questionAttemptEntities,
                );
              }),
            );
          }),
        );
      }),
      shareOneReplay(),
    );

  readonly allQuestionAttemptsDone$ = this.questionsWithAttemptStatuses$.pipe(
    map((questions) => {
      return questions.every((question) => question.attemptStatus === 'DONE');
    }),
    distinctUntilChanged(),
    shareOneReplay(),
  );

  readonly nextUnfinishedQuestion$: Observable<AssignmentSectionQuestionUIModel | null> = combineLatest([
    this.questionsWithAttemptStatuses$,
    this.assignmentSectionQuestionQuery.active$,
  ]).pipe(
    map(([questions, activeQuestion]) => {
      if (!activeQuestion) {
        return null;
      }

      const activeQuestionIndex = questions.findIndex(
        (question) => question.assignmentSectionQuestion.question_id === activeQuestion.question_id,
      );

      const nextUnfinishedQuestion = questions
        .slice(activeQuestionIndex + 1)
        .find((question) => question.attemptStatus !== 'DONE');

      if (!nextUnfinishedQuestion) {
        const previousUnfinishedQuestion = questions
          .slice(0, activeQuestionIndex)
          .find((question) => question.attemptStatus !== 'DONE');
        return previousUnfinishedQuestion?.assignmentSectionQuestion ?? null;
      }

      return nextUnfinishedQuestion.assignmentSectionQuestion;
    }),
    shareOneReplay(),
  );

  setChallengeQuestion(
    question: AssignmentSectionQuestionUIModel,
    challengeActivityId: ChallengeActivityUIModel['id'],
  ): Observable<void> {
    if (this.assignmentSectionQuestionService.isQuestionActive(question.assignment_id, question.question_id)) {
      return EMPTY;
    }

    return this.challengeActivityService.setCurrentQuestion(challengeActivityId, question.question_id);
  }

  syncQuestionAttemptWithOngoingChallengeActivity() {
    return combineLatest([
      this.challengeActivityQuery.ongoingActivity$.pipe(
        filter((challengeActivity) => !!challengeActivity),
        distinctUntilKeyChanged('current_question_id'),
      ),
      this.assignmentSectionQuestionQuery.entities$.pipe(filter((questions) => questions.length > 0)),
    ]).pipe(
      switchMap(([challengeActivity]) => {
        this.isTransitioningBetweenQuestions.set(true);
        return this.selectQuestionAttemptWithQuestionId(challengeActivity).pipe(
          finalize(() => {
            this.isTransitioningBetweenQuestions.set(false);
          }),
        );
      }),
    );
  }

  private selectQuestionAttemptWithQuestionId(challengeActivity: ChallengeActivityUIModel) {
    if (!challengeActivity.current_question_id || !challengeActivity.id) {
      return EMPTY;
    }
    const questionAttemptByQuestionId = this.challengeActivityQuestionAttemptQuery.getByQuestionId(
      challengeActivity.id,
      challengeActivity.current_question_id,
    );

    this.assignmentSectionQuestionService.setActiveQuestion(
      challengeActivity.assignment_section_id,
      challengeActivity.current_question_id,
    );

    if (questionAttemptByQuestionId) {
      return this.selectExistingChallengeActivityQuestionAttempt(challengeActivity, questionAttemptByQuestionId);
    } else {
      return this.challengeActivityService
        .getOrCreateChallengeQuestionAttempt(challengeActivity.id, challengeActivity.current_question_id)
        .pipe(
          switchMap((attemptId) => {
            return this.challengeActivityQuestionAttemptService.fetchByQuestionAttemptId(
              attemptId,
              challengeActivity.id,
            );
          }),
          switchMap((attempt: ChallengeActivityQuestionAttemptStoreModel | null) => {
            if (!attempt) {
              return EMPTY;
            }
            return this.selectExistingChallengeActivityQuestionAttempt(challengeActivity, attempt);
          }),
        );
    }
  }

  private mapQuestionAttemptsToQuestionsWithAttemptStatuses(
    questions: AssignmentSectionQuestionUIModel[],
    attempts: ChallengeActivityQuestionAttemptStoreModel[],
    questionAttemptEntities: QuestionAttemptEntityUIModel[],
  ) {
    return questions.map((question) => {
      const questionAttemptEntity = questionAttemptEntities.find(
        (entity) => entity.attempt.question_id === question.question_id,
      );
      return {
        assignmentSectionQuestion: question,
        attemptStatus: attempts.find((attempt) => attempt.question_id === question.question_id)?.status ?? 'TODO',
        questionAttemptStatus: questionAttemptEntity?.attempt.status ?? null,
        questionGradingResult: questionAttemptEntity?.attempt.grading_result
          ? {
              awardedMarks: getGradingResultAwardedMarksFromQuestionItems(questionAttemptEntity.questionItems),
              totalMarks: calculateQuestionTotalMarks(
                questionAttemptEntity.questionItems,
                questionAttemptEntity.markCalculationMethod,
              ),
            }
          : null,
      };
    });
  }

  private selectExistingChallengeActivityQuestionAttempt(
    challengeActivity: ChallengeActivityUIModel | null,
    questionAttemptByQuestionId: ChallengeActivityQuestionAttemptStoreModel | null,
  ) {
    if (
      !challengeActivity?.current_question_id ||
      !challengeActivity?.id ||
      !questionAttemptByQuestionId?.question_attempt_id
    ) {
      return EMPTY;
    }

    this.challengeActivityQuestionAttemptService.setActive(challengeActivity.id, challengeActivity.current_question_id);

    if (this.questionAttemptQuery.hasEntity(questionAttemptByQuestionId.question_attempt_id)) {
      this.questionAttemptEntityService.setActive(questionAttemptByQuestionId.question_attempt_id);
      return EMPTY;
    }

    return this.questionAttemptEntityService.fetchQuestionAttemptDetails(
      questionAttemptByQuestionId.question_attempt_id,
      {
        setActive: true,
      },
    );
  }
}
